// @flow

import { createSelector } from 'reselect';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectProductErrors,
  selectIsAnyCartProductWorking,
  hasValidatedAllContracts as selectHasValidatedAllContracts,
  selectDomainCreateOrTransferProducts,
  selectRawProductsArray,
} from '@gandi/cart';

import { selectCurrentId } from '@gandi/redux-sharing';

import { selectProductsWithDomainContactError } from 'src/Cart/DomainContacts/Wizard/modules/selectors';

import {
  selectIsAppWorking,
  selectIsPatchingDomainContacts,
  selectIsPostingCheckout,
} from '../../modules/app';

import type { FullState } from '../../types';

const selectHasProductErrors: (state: FullState) => boolean = createSelector(
  [selectProductErrors],
  errors => errors.length > 0,
);

export const selectShouldShowContactWizardAlert = (
  state: FullState,
): boolean => {
  const authenticated = selectIsAuthenticatedUser(state);
  // User is unauthenticated, pass.
  if (!authenticated) {
    return false;
  }

  const patchingDomainContacts = selectIsPatchingDomainContacts(state);
  const productsWithDomainContactErrors =
    selectProductsWithDomainContactError(state);
  return !patchingDomainContacts && productsWithDomainContactErrors.length > 0;
};

export const selectShouldShowOtherDomainErrors = (
  state: FullState,
): boolean => {
  const authenticated = selectIsAuthenticatedUser(state);
  if (!authenticated) {
    return false;
  }

  const patchingDomainContacts = selectIsPatchingDomainContacts(state);
  const productsWithDomainContactErrors =
    selectProductsWithDomainContactError(state);

  return !patchingDomainContacts && !productsWithDomainContactErrors.length;
};

export const selectIsCheckoutButtonDisabled: (state: FullState) => boolean =
  createSelector(
    [
      selectCurrentId,
      selectIsAppWorking,
      selectIsAnyCartProductWorking,
      selectHasProductErrors,
      selectHasValidatedAllContracts,
      selectIsPostingCheckout,
    ],
    (
      currentId,
      isAppWorking,
      isAnyCartProductWorking,
      hasProductErrors,
      hasValidatedAllContracts,
      isPostingCheckout,
    ) => {
      // User is unauthenticated, pass
      if (!currentId) {
        return false;
      }

      const loading =
        isAppWorking || isAnyCartProductWorking || isPostingCheckout;
      return loading || hasProductErrors || !hasValidatedAllContracts;
    },
  );

export const selectNeedDomainConfiguration = (state: FullState): boolean => {
  const domains = selectDomainCreateOrTransferProducts(state);
  const hostingWithDomain = selectRawProductsArray(state).filter(
    p => p.productType === 'paas' && !!p.payload.domain,
  );

  return (
    domains.some(domain => !domain.payload?.owner) ||
    hostingWithDomain.some(hosting => !hosting.payload.domain?.owner)
  );
};
